import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface ReferralLinkRowProps {
  /** Read-only link shown in the input. */
  url: string;
  /** True while this row's copy button is in its "copied" state. */
  copied: boolean;
  onCopy: () => void;
  /** Prefix for this row's `data-testid`s, e.g. `affiliate-referral`. */
  testIdPrefix: string;
  label?: string;
  /**
   * Short highlights beside the label — the commission rates this link earns.
   * A link can earn on more than one product, so this is a list.
   */
  badges?: string[];
  hint?: string;
}

/**
 * One shareable referral link: read-only field + copy button. Rendered once per
 * destination (the app funnel and the Viralize course landing), so the two rows
 * cannot drift apart.
 */
export const ReferralLinkRow = ({
  url,
  copied,
  onCopy,
  testIdPrefix,
  label,
  badges,
  hint,
}: ReferralLinkRowProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      {(label || badges?.length) && (
        <div className="flex flex-wrap items-center gap-2">
          {label && <p className="text-sm font-medium">{label}</p>}
          {badges?.map((badge, index) => (
            <Badge
              key={badge}
              variant="secondary"
              data-testid={`${testIdPrefix}-badge-${index}`}
            >
              {badge}
            </Badge>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input readOnly value={url} data-testid={`${testIdPrefix}-url`} />
        <Button variant="secondary" onClick={onCopy} data-testid={`${testIdPrefix}-copy-button`}>
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          <span className="ml-2">{t('affiliates.link.copy')}</span>
        </Button>
      </div>
      {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
};
